import React, { useEffect, useRef } from "react";

// Single source of truth for the preset emoji palette.
// Shared across ALL experimental conditions — do NOT branch by condition.
// Curated to let participants express the FULL range of plausible reactions:
// support/agreement, openness/thinking, humor/laughter, frustration/anger,
// dismissal/disregard, sadness/concern, and emphasis/surprise — balanced,
// not skewed positive/hype.
export const PRESET_EMOJIS = [
  // support / agreement
  '👍', '❤️', '🙌', '👏', '💪', '🤝', '🙂',
  // openness / thinking
  '🤔', '💭', '🤷', '👀', '🙏',
  // humor / laughter (genuine amusement AND mockery/derision)
  '😂', '🤣', '😆', '😅',
  // frustration / anger
  '😤', '😠', '😡', '🤬', '😩', '😮‍💨',
  // dismissal / disregard
  '🙄', '😒', '👎', '🤡', '💀',
  // sadness / concern
  '😢', '😔', '😟', '😬',
  // emphasis / surprise
  '😮', '‼️', '🔥', '💯',
];

/**
 * Presentational emoji picker.
 * The parent owns the textarea state and appends the selected emoji.
 *
 * Props:
 *  - onSelect(emoji): called with the chosen emoji.
 *  - onClose(): called when clicking outside the picker.
 *  - disabled (optional): when true, buttons are non-interactive (e.g. at length limit).
 */
const EmojiPicker = ({ onSelect, onClose, disabled = false }) => {
  const pickerRef = useRef(null);

  // Click-outside-to-close (ported from PostMessage.js).
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        onClose?.();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  return (
    <div
      ref={pickerRef}
      className="bg-spiders-bg-elevated rounded-xl border border-spiders-border p-3 mb-3 shadow-lg"
    >
      <div className="grid grid-cols-8 gap-0.5">
        {PRESET_EMOJIS.map((emoji, index) => (
          <button
            key={index}
            type="button"
            disabled={disabled}
            onClick={() => onSelect?.(emoji)}
            className="text-xl hover:bg-spiders-primary-500/20 hover:scale-125 rounded-lg p-2 transition-all duration-150 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:hover:bg-transparent"
          >
            {emoji}
          </button>
        ))}
      </div>
    </div>
  );
};

export default EmojiPicker;
